/**
 * Sprint S13.1.A — onboarding wizard top-level page.
 *
 * Layout: progress indicator → current step content → back/next nav.
 * Hydration: on mount fetches `GET /api/onboarding/profile`. If profile
 * exists с `informed_consent_signed=true`, redirects к "/" (already
 * onboarded). Otherwise hydrates store + jumps к first incomplete step.
 *
 * S13.1.A renders only Step1Welcome + Step8Submit fully; intermediate
 * steps (2-7) показывают StepStub. S13.1.B replaces stubs.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/Card";
import {
  useOnboardingStore,
  STEP_ORDER,
  findFirstIncompleteStep,
} from "@/stores/onboardingStore";
import { getOnboardingProfile } from "@/api/onboarding";
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress";
import { OnboardingNavigation } from "@/components/onboarding/OnboardingNavigation";
import { Step1Welcome } from "@/components/onboarding/Step1Welcome";
import { Step2HealthContext } from "@/components/onboarding/Step2HealthContext";
import { Step3Lifestyle } from "@/components/onboarding/Step3Lifestyle";
import { Step4MentalPregnancy } from "@/components/onboarding/Step4MentalPregnancy";
import { Step5SportProfile } from "@/components/onboarding/Step5SportProfile";
import { Step6Schedule } from "@/components/onboarding/Step6Schedule";
import { Step7Goals } from "@/components/onboarding/Step7Goals";
import { Step8Submit } from "@/components/onboarding/Step8Submit";
import { StepStub } from "@/components/onboarding/StepStub";


export default function OnboardingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = useOnboardingStore((s) => s.data);
  const currentStepIndex = useOnboardingStore((s) => s.currentStepIndex);
  const isHydrated = useOnboardingStore((s) => s.isHydrated);
  const setData = useOnboardingStore((s) => s.setData);
  const setHydrated = useOnboardingStore((s) => s.setHydrated);
  const goToStep = useOnboardingStore((s) => s.goToStep);

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      try {
        const profile = await getOnboardingProfile();
        if (cancelled) return;

        if (profile) {
          // Already onboarded — bounce к dashboard
          if (profile.informed_consent_signed) {
            navigate("/", { replace: true });
            return;
          }
          // Hydrate draft from server (resume support)
          setData(profile);
          goToStep(findFirstIncompleteStep(profile));
        }
        setHydrated(true);
      } catch (err) {
        // Network / 5xx — still allow user к proceed с DEFAULT_ONBOARDING
        // eslint-disable-next-line no-console
        console.error("Onboarding hydration failed:", err);
        setHydrated(true);
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // S13.1.B.2 — focus management on step transition: scroll к top + auto-focus
  // step heading (id="step-heading", tabIndex=-1) for screen-reader announcement.
  useEffect(() => {
    if (!isHydrated) return;
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    const id = window.setTimeout(() => {
      const heading = document.getElementById("step-heading");
      heading?.focus();
    }, 100);
    return () => window.clearTimeout(id);
  }, [currentStepIndex, isHydrated]);

  // S13.1.B.2 — keyboard nav: Enter advances "Next" outside form fields.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (document.activeElement?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (tag === "BUTTON") return; // browser native handles button Enter
      if (e.key === "Enter") {
        const nextBtn = document.querySelector<HTMLButtonElement>(
          "[data-onboarding-next]:not([disabled])",
        );
        if (nextBtn) {
          e.preventDefault();
          nextBtn.click();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  if (!isHydrated) {
    return (
      <div className="flex h-screen items-center justify-center bg-cream">
        <div className="text-[14px] text-taupe">
          {t("onboarding.loading")}
        </div>
      </div>
    );
  }

  const currentStepId = STEP_ORDER[currentStepIndex];

  let StepComponent: React.ComponentType;
  switch (currentStepId) {
    case "welcome":
      StepComponent = Step1Welcome;
      break;
    case "health_context":
      StepComponent = Step2HealthContext;
      break;
    case "lifestyle":
      StepComponent = Step3Lifestyle;
      break;
    case "mental_pregnancy":
      StepComponent = Step4MentalPregnancy;
      break;
    case "sport_profile":
      StepComponent = Step5SportProfile;
      break;
    case "schedule":
      StepComponent = Step6Schedule;
      break;
    case "goals":
      StepComponent = Step7Goals;
      break;
    case "consent_submit":
      StepComponent = Step8Submit;
      break;
    default:
      StepComponent = StepStub;
  }

  return (
    <div className="min-h-screen bg-cream py-6 sm:py-10">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <h1 className="mb-2 text-2xl font-semibold text-navy-deep sm:text-3xl">
          {t("onboarding.title")}
        </h1>
        <p className="mb-6 text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.subtitle")}
        </p>

        <OnboardingProgress
          currentIndex={currentStepIndex}
          total={STEP_ORDER.length}
        />

        <Card className="p-6">
          <StepComponent />
        </Card>

        <OnboardingNavigation />

        <p className="mt-8 text-center text-[12px] text-taupe">
          {t("onboarding.draft_note", {
            defaultValue:
              "Your progress is saved automatically and will resume на next visit.",
          })}
        </p>

        {/* Provide quick reference to data field while still в S13.1.A */}
        {data.first_name ? (
          <p className="mt-2 text-center text-[11px] text-taupe">
            {t("onboarding.session_user", {
              name: data.first_name,
              defaultValue: "Resuming session for {{name}}",
            })}
          </p>
        ) : null}
      </div>
    </div>
  );
}
